import { createColumnHelper } from '@tanstack/react-table';
import { __ } from '@wordpress/i18n';
import { ShareButton } from '../ShareButton';

const col = createColumnHelper();

const taxNames = ( connection ) =>
	connection?.nodes?.map( ( n ) => n.name ).join( ', ' ) ?? '';

function taxFilterFn( row, columnId, filterValue ) {
	const connection = row.original[ columnId ];
	return connection?.nodes?.some( ( n ) => n.name === filterValue ) ?? false;
}
taxFilterFn.autoRemove = ( val ) => ! val;

function stringEqualsFilterFn( row, columnId, filterValue ) {
	return row.getValue( columnId ) === filterValue;
}
stringEqualsFilterFn.autoRemove = ( val ) => ! val;

// Filters rows where row.original[columnId] is a string[] that includes filterValue.
function stringArrayIncludesFilterFn( row, columnId, filterValue ) {
	return row.original[ columnId ]?.includes( filterValue ) ?? false;
}
stringArrayIncludesFilterFn.autoRemove = ( val ) => ! val;

/**
 * Column definitions for the person CPT.
 * memberOrgs, memberAdmins, and memberPositions are string[] injected by PersonTable from the memberships REST endpoint.
 * @param root0
 * @param root0.onDelete
 */
export function peopleColumns( { onDelete } ) {
	return [
		col.display( {
			id: 'actions',
			header: '',
			cell: ( { row } ) => (
				<div className="flex flex-col gap-1 items-start">
					{ window.anacrm?.isStaff && (
						<button
							onClick={ () => {
								if (
									! window.confirm(
										__(
											'Move this person to trash?',
											'anacrm'
										)
									)
								) {
									return;
								}
								onDelete( row.original.databaseId );
							} }
							className="px-2 py-1 text-xs border-2 border-red bg-white hover:bg-red hover:text-white cursor-pointer font-semibold whitespace-nowrap"
						>
							{ __( 'Delete', 'anacrm' ) }
						</button>
					) }
					<ShareButton uri={ row.original.uri } />
				</div>
			),
		} ),

		col.accessor( 'firstname', {
			header: __( 'Firstname', 'anacrm' ),
			cell: ( { row, getValue } ) => (
				<a
					href={ row.original.uri }
					className="font-semibold text-blue hover:!no-underline"
				>
					{ getValue() }
				</a>
			),
		} ),

		col.accessor( 'lastname', {
			header: __( 'Lastname', 'anacrm' ),
			cell: ( { getValue } ) => getValue(),
		} ),

		col.accessor( 'title', {
			header: '',
			enableSorting: false,
			enableColumnFilter: false,
			meta: { hidden: true },
		} ),

		col.accessor( ( row ) => taxNames( row.territories ), {
			id: 'territories',
			header: __( 'Territory', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.cities ), {
			id: 'cities',
			header: __( 'Municipality', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( 'contactMail', {
			header: __( 'Email', 'anacrm' ),
			cell: ( { getValue } ) => {
				const v = getValue();
				if ( ! v ) {
					return '—';
				}
				const emails = v
					.split( ',' )
					.map( ( e ) => e.trim() )
					.filter( Boolean );
				return (
					<div className="flex flex-col gap-1">
						{ emails.map( ( email ) => (
							<a
								key={ email }
								href={ `mailto:${ email }` }
								className="text-blue hover:!no-underline"
							>
								{ email }
							</a>
						) ) }
					</div>
				);
			},
		} ),

		col.accessor( 'contactPhone', {
			header: __( 'Phone', 'anacrm' ),
			cell: ( { getValue } ) => {
				const v = getValue();
				if ( ! v ) {
					return '—';
				}
				const phones = v
					.split( ',' )
					.map( ( p ) => p.trim() )
					.filter( Boolean );
				return (
					<div className="flex flex-col gap-1">
						{ phones.map( ( phone ) => (
							<span key={ phone }>{ phone }</span>
						) ) }
					</div>
				);
			},
		} ),

		col.accessor( ( row ) => taxNames( row.sectors ), {
			id: 'sectors',
			header: __( 'Sector', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => row.memberOrgs?.join( ', ' ) ?? '', {
			id: 'memberOrgs',
			header: __( 'Organisation', 'anacrm' ),
			filterFn: stringArrayIncludesFilterFn,
		} ),

		col.accessor( ( row ) => row.memberAdmins?.join( ', ' ) ?? '', {
			id: 'memberAdmins',
			header: __( 'Administration', 'anacrm' ),
			filterFn: stringArrayIncludesFilterFn,
		} ),

		col.accessor( ( row ) => row.memberPositions?.join( ', ' ) ?? '', {
			id: 'memberPositions',
			header: __( 'Position', 'anacrm' ),
			filterFn: stringArrayIncludesFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.genders ), {
			id: 'genders',
			header: __( 'Gender', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( 'yearBirth', {
			header: __( 'Birth year', 'anacrm' ),
			cell: ( { getValue } ) => getValue() || '—',
		} ),

		col.accessor( ( row ) => taxNames( row.workConditions ), {
			id: 'workConditions',
			header: __( 'Employment status', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor(
			( row ) =>
				row.permissionContact
					? __( 'Yes', 'anacrm' )
					: __( 'No', 'anacrm' ),
			{
				id: 'permissionContact',
				header: __( 'Permission', 'anacrm' ),
				filterFn: stringEqualsFilterFn,
			}
		),

		col.accessor( ( row ) => taxNames( row.interests ), {
			id: 'interests',
			header: __( 'Interests', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),
	];
}
